import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Header, Icon, Breadcrumb, Table, Grid, Placeholder, Segment, Message, Dropdown } from 'semantic-ui-react';
import moment, { Moment } from 'moment';
import { render } from '@testing-library/react';

export interface TimeSlotData {
    label: string;
    supperScriptLable?: string;
    timeSlots: SlotData[];
}

export interface SlotData {
    startTime: string;
    endTime: string;
    isTaken: boolean;
    slotId: string;
    meetingId?: number;
    meetingLength?: number;
}

export interface TimeSlotProps {
    isReadOnly?: boolean;
    selectedDate: string;
    meetingsList: MeetingEvent[];
    onTimeChange?: Function;
}

export interface MeetingEvent {
    meetingId: number;
    startTime: string;
    endTime: string;
    requesterName: string;
}

export const TimeSlot: React.FC<TimeSlotProps> = (props: TimeSlotProps) => {
    const slotTimeInterval = 30;
    const rowSlotsCount = 2;
    const slotHeight = 2;
    const dayStartTime = '08:00 AM';
    const dayEndTime = '04:00 PM';
    const isReadOnly: boolean = props.isReadOnly || false;

    const [isMousedown, setIsMousedown] = useState<boolean>(false);
    const [selectedSlots, setSelectedSlots] = useState<Map<string,boolean>>(new Map<string,boolean>());
    const [takenSlotMap, setTakenSlotMap] = useState<Map<string,boolean>>(new Map<string,boolean>());
    const [renderCounter, setRenderCounter] = useState(0);
    const slotArrayRef = useRef<SlotData[]>([]);
    const [data, setData] = useState<TimeSlotData[]>();

    const selectedStartTime = useRef<string>(moment(props.selectedDate + dayEndTime).add(1, 'minute').format());
    const selectedEndTime = useRef<string>(moment(props.selectedDate + dayStartTime).add(-1,'minute').format());

    useEffect(() => {
        let newRenderData = getDataArray(props);
        setData(newRenderData);
    }, [props]);

    const getDataArray = (props: TimeSlotProps): TimeSlotData[] => {
        let startTimeOfDay: Moment = moment(props.selectedDate + dayStartTime);
        let endTimeOfDay: Moment = moment(props.selectedDate + dayEndTime);
        let currTime = moment(props.selectedDate + dayStartTime);
        let timeArray: string[] = [];
        if (currTime.isValid()) {
            while (currTime.isSameOrBefore(endTimeOfDay, 'minute')) {
                timeArray.push(currTime.format());
                currTime = currTime.add(slotTimeInterval, 'minute');
            }
        };
        let slotData = [];
        for (let i = 1; i < timeArray.length; i++) {
            let tmepSlotData: SlotData = {
                startTime: timeArray[i - 1],
                endTime: timeArray[i],
                isTaken: false,
                slotId: 'time-slot-' + (i - 1).toString(),
                meetingId: 0,
            };
            props.meetingsList.forEach((meeting)=>{
                let meetingStartTime = moment(meeting.startTime);
                let meetingEndTime = moment(meeting.endTime);
                let slotStartTime = moment(timeArray[i - 1]);
                let slotEndTime = moment(timeArray[i]);
                
                if(meetingStartTime.isSameOrBefore(slotStartTime) &&
                    meetingEndTime.isSameOrAfter(slotEndTime)
                ){
                    tmepSlotData.isTaken = true;
                }
                if(meetingStartTime.isSame(slotStartTime,'minute')){
                    tmepSlotData.meetingId = meeting.meetingId;
                    // compute meeting length
                    tmepSlotData.meetingLength = Math.floor(meetingEndTime.diff(meetingStartTime, 'minute') / slotTimeInterval);
                }
            });
            slotData.push(tmepSlotData);
        }
        slotArrayRef.current = slotData;

        let timeSlotDataArray: TimeSlotData[] = [];
        let currIndex = 0;
        let rowFilledCount = 0;
        let currObj: TimeSlotData = {
            label: '',
            timeSlots: []
        };
        while (currIndex < slotData.length) {
            if (rowFilledCount === 0) {
                currObj = {
                    label: moment(slotData[currIndex].startTime).format('h:mm'),
                    supperScriptLable: moment(slotData[currIndex].startTime).format('A'),
                    timeSlots: [],
                };
            }
            currObj.timeSlots.push(slotData[currIndex]);
            rowFilledCount += 1;
            if (rowFilledCount === rowSlotsCount) {
                timeSlotDataArray.push(currObj);
                rowFilledCount = 0;
            }
            currIndex += 1;
        }
        return timeSlotDataArray;
    }

    function reRender() {
        setRenderCounter((prevValue)=>{return prevValue + 1});
    }

    function handleMouseEnter(elementId: string, isTaken: boolean = false) {
        //console.log( (event.target as HTMLElement).id);
        let thisSlot = slotArrayRef.current.filter((item)=>{return item.slotId === elementId})[0];
        if (isMousedown && thisSlot.isTaken == false) {
            if(moment(thisSlot.startTime).isSameOrBefore(moment(selectedStartTime.current), 'minute')){
                selectedStartTime.current = thisSlot.startTime;
            }
            if(moment(thisSlot.endTime).isSameOrAfter(moment(selectedEndTime.current), 'minute')){
                selectedEndTime.current = thisSlot.endTime;
            }
            reRender();
            selectedSlots.set(elementId, true);
        } else {
            if (isTaken && selectedSlots.size > 0) {
                finishSelect();
            }
        }
    }

    function handleMouseDown(elementId:string) {
        let thisSlot = slotArrayRef.current.filter((item)=>{return item.slotId === elementId})[0];
        if ( thisSlot.isTaken == false) {
            // slot can not be already taken
            // set selected list to new.
            let newMap = new Map<string, boolean>();
            newMap.set(elementId, true);
            setSelectedSlots(newMap);
            
            selectedStartTime.current = moment(props.selectedDate + dayEndTime).add(1, 'minute').format();
            selectedEndTime.current = moment(props.selectedDate + dayStartTime).add(-1, 'minute').format();

            setIsMousedown(true);
            let thisSlot = slotArrayRef.current.filter((item)=>{return item.slotId === elementId})[0];
            if(moment(thisSlot.startTime).isSameOrBefore(moment(selectedStartTime.current), 'minute')){
                selectedStartTime.current = thisSlot.startTime;
            }
            if(moment(thisSlot.endTime).isSameOrAfter(moment(selectedEndTime.current), 'minute')){
                selectedEndTime.current = thisSlot.endTime;
            }
            window.document.addEventListener('mouseup', handleMouseUp);
            window.document.getElementById('time-slot-wrapper')?.addEventListener('mouseleave', handleMouseLeaveWrapper);
        }

    }

    function handleMouseLeaveWrapper(event?: MouseEvent | React.MouseEvent){
        finishSelect();
    };

    function handleMouseUp(event?: MouseEvent) {
        if( event && (event.target as HTMLElement).tagName !== 'SPAN'
            && event && (event.target as HTMLElement).className == 'time-slot-slot-box selected'
        ){
            finishSelect();
        }
    }

    function finishSelect(){
        setIsMousedown(false);
        props.onTimeChange && props.onTimeChange({
            startTime: selectedStartTime.current,
            endTime: selectedEndTime.current
        });
        window.removeEventListener('mouseup', handleMouseUp);
        window.document.getElementById('time-slot-wrapper')?.removeEventListener('mouseleave', handleMouseLeaveWrapper);
    }

    useEffect(() => {
        return () => {
            window.document.removeEventListener('mouseup', handleMouseUp);
        }
    }, []);

    return <div className='time-slot-wrapper' id='time-slot-wrapper'>
        <div><b>Time Slots</b></div>
        {
            data && data.length > 0 &&
            data.map((rowData, rowIndex) => {
                return (
                    <div className='time-slot-row' key={rowIndex}>
                        <div className='time-slot-row-label' style={{height:`${rowSlotsCount * slotHeight}rem`}}>
                            <span><b>{rowData.label}</b>{rowData.supperScriptLable ? <sup>{rowData.supperScriptLable}</sup> : ''}</span>
                        </div>
                        <div className='time-slot-row-slots-container' style={{height:`${rowSlotsCount * slotHeight}rem`}}>
                            {
                                rowData.timeSlots.length > 0 && rowData.timeSlots.map((slot, slotIndex) => {
                                    let isSelected: boolean = selectedSlots.get(slot.slotId) !== undefined;
                                    let meetingData = undefined;
                                    if(slot.meetingId !== undefined){
                                        meetingData = props.meetingsList.filter((meeting)=>{return meeting.meetingId == slot.meetingId})[0];
                                    }
                                    if(slot.isTaken){
                                        return (
                                            <div id={slot.slotId}
                                                style={{height:`${slotHeight}rem`}}
                                                className={ 'time-slot-slot-box' + (isSelected ? ' selected' : '') + (slot.isTaken ? ' taken':'')} 
                                                key={slot.slotId}
                                                onMouseEnter={()=>{handleMouseEnter(slot.slotId,true)}}
                                            >
                                                {
                                                    slot.meetingId !== 0 &&
                                                    <div className='meeting-container' 
                                                    onMouseEnter={()=>{handleMouseEnter(slot.slotId, true)}}
                                                    >
                                                        <Segment className='meeting-container-segment' 
                                                            inverted
                                                            title={'Slot taken by other meeting'}
                                                            style={{height:`${slot.meetingLength as number *  slotHeight}rem`}}
                                                            >
                                                                {
                                                                    meetingData && meetingData.requesterName && 
                                                                    <span><b>Meeting with {(meetingData as MeetingEvent)?.requesterName || ''}</b></span>
                                                                }
                                                        </Segment>
                                                    </div>
                                                }
                                            </div>
                                        );
                                    }
                                    else {
                                        return (
                                            <div id={slot.slotId}
                                                style={{height:`${slotHeight}rem`}}
                                                title={ isReadOnly ? `${moment(slot.startTime).format('hh:mm A')} - ${moment(slot.endTime).format('hh:mm A')}, no meeting scheduled` : `${moment(slot.startTime).format('hh:mm A')} - ${moment(slot.endTime).format('hh:mm A')}, drag to select multiple slots`}
                                                className={ 'time-slot-slot-box' + (isSelected ? ' selected' : '') + (slot.isTaken ? ' taken':'')}
                                                key={slot.slotId}
                                                onMouseDown={()=>{ 
                                                    if(!isReadOnly){
                                                        handleMouseDown(slot.slotId)}
                                                    }
                                                }
                                                onMouseEnter={()=>{handleMouseEnter(slot.slotId)}}
                                            >
                                            </div>
                                        );
                                    }
                                    
                                })
                            }
                        </div>
                    </div>);
            })
        }
        {
            selectedSlots.size > 0 &&
            <p className='mt-2'><b>You have selected: {moment(selectedStartTime.current).format('hh:mm A')} - {moment(selectedEndTime.current).format('hh:mm A')}</b></p>
        }
        
    </div>
}